'use client';
import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useQueryAll } from '../apolloClient';
import { useScreenSize } from "../hooks/ScreenSizeContext";
import { DateTime } from "luxon";

import Card from '@mui/material/Card';
import Grid from "@mui/material/Grid";
import TextField from '@mui/material/TextField';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import MDBox from './MDBox/MDBoxRoot';
import MDButton from './MDButton/MDButtonRoot';
import StyledBox from './StyleBox';
import setThemeObj from './../hooks/useThemeMUI';
import { SchoolTime } from '../utils/SchoolTime';
import * as types from '../gql/graphql';

const getInstructorEventsQuery = gql(`
  query GetInstructorEvents($after: Cursor, $from: Datetime, $to: Datetime) {
    fs_eventsCollection(
      filter: {and: [{date: {gte: $from}}, {date: {lte: $to}}]}
      orderBy: {date: DescNullsFirst}
      after: $after
    ) {
      edges {
        node {
          id
          fs_instructoreventsCollection { 
            pageInfo {endCursor hasNextPage}
            edges {
              node { 
                on_deck
                instructor
                fs_admins {first_name last_name}
            }}}
          date
          fs_classes {
            name
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const tableHeaders = [
  'Instructor ID', 'First Name', 'Last Name', 'Class Name', 'Date', 'Ondeck'
];

const rowKeys = [
  'InstructorID', 'FirstName', 'LastName', 'ClassName', 'Date', 'Ondeck'
];

const formatInstructorEventRow = (event: types.Fs_EventsEdge) => {
  const instructors = event.node.fs_instructoreventsCollection.edges;
  const rows = [];

  instructors.map((i)=>{rows.push({
    id: i.node.id, 
    InstructorID: i.node.instructor, 
    FirstName: i.node.fs_admins.first_name, 
    LastName: i.node.fs_admins.last_name, 
    ClassName: event.node.fs_classes.name, 
    Date: event.node.date? SchoolTime.fromISO(event.node.date).toShortDateString() : '', 
    Ondeck: i.node.on_deck?"On Deck":""})})

  return rows;
};

const InstructorEventsReport = () => {
  const today = SchoolTime.now();
  const start = today.toDateTime().minus({ years: 1 }).set({ month: 11, day: 1 }).startOf('day');
  const end = today.toDateTime().set({ month: 10, day: 31 }).endOf('day');

  const [fromInput, setFromInput] = useState(start.toISODate());
  const [toInput, setToInput] = useState(end.toISODate());
  const [dateFilter, setDateFilter] = useState({ from: SchoolTime.fromDateTime(start), to: SchoolTime.fromDateTime(end) });

  const { loading, error, data } = useQueryAll<types.Fs_EventsEdge>({
    query: getInstructorEventsQuery,
    variables: { from: dateFilter.from.toISOLocal(), to: dateFilter.to.toISOLocal() },
  });

  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromInput(e.target.value);
  };

  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToInput(e.target.value);
  };

  const handleRunReport = () => {
    const fromDate = SchoolTime.fromISODate(fromInput);
    const toDate = SchoolTime.fromISODate(toInput);
    setDateFilter({ from: fromDate, to: toDate });
  };

  const downloadCSV = (rows) => {
    const headers = tableHeaders.join(",");
    const csvRows = rows.map(row =>
      rowKeys.map(key => {
        const cell = row[key];
        // Escape quotes
        const safe = typeof cell === 'string' ? `"${cell.replace(/"/g, '""')}"` : cell;
        return safe;
      }).join(",")
    );

    const csvContent = [headers, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Instructor_event_report_${fromInput}_to_${toInput}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <MDBox p={2}>Loading...</MDBox>;
  if (error) return <MDBox p={2}>Error loading events</MDBox>;

  const allRows = [];

  for (const event of data) {
    const rows = formatInstructorEventRow(event); // returns an array of rows
    allRows.push(...rows);
  }


  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Card>
          <MDBox component="main" px={4} py={4}>
            <StyledBox theme={setThemeObj} sx={{ marginBottom: 2 }}>
              <Grid container alignItems="flex-end" justifyContent="space-between">
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="From:"
                    type="date"
                    value={fromInput}
                    onChange={handleFromDateChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />

                  <TextField
                    label="To:"
                    type="date"
                    value={toInput}
                    onChange={handleToDateChange}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                  />

                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={handleRunReport}
                    sx={{ height: '56px', whiteSpace: 'nowrap' }}
                  >
                    Run Report
                  </MDButton>
                </Grid>

                <Grid item>
                  <MDButton
                    variant="contained"
                    color="secondary"
                    onClick={() => downloadCSV(allRows)}
                    sx={{ height: '56px', whiteSpace: 'nowrap' }}
                  >
                    Download
                  </MDButton>
                </Grid>
              </Grid>
            </StyledBox>

            <MDBox sx={{ maxHeight: '800px', overflowY: 'auto' }}>
              <MDBox component="table" sx={{ borderSpacing: "0px", width: "100%" }}>
                <MDBox
                  component="thead"
                  ownerState={{ variant: "contained", bgColor: "light" }}
                  sx={{
                    '& td': { fontSize: '12px' },
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 1,
                  }}
                  borderRadius={2}
                >
                  <MDBox component="tr">
                    {tableHeaders.map(header => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </MDBox>
                </MDBox>

                <TableBody sx={{ '& td': { fontSize: '12px' } }}>
                  {allRows.map(instructorEvent => {
                    return (
                      <TableRow key={instructorEvent.id}>
                        {rowKeys.map(key => (
                          <TableCell key={key}>{instructorEvent[key]}</TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </MDBox>
            </MDBox>
          </MDBox>
        </Card>
      </Grid>
    </Grid>
  );
};

export default InstructorEventsReport;
